import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import type { DatePickerProps } from "@mui/x-date-pickers/DatePicker";
import type { PickerDayProps } from "@mui/x-date-pickers/PickerDay";
import { PickerDay } from "@mui/x-date-pickers/PickerDay";
import type React from "react";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { INK } from "@/theme/tokens";
import { portalOverlayProps } from "./portalOverlay";

type Props = {
	overridePickerProps?: DatePickerProps;
	value?: DatePickerProps["value"];
	defaultValue?: DatePickerProps["defaultValue"];
	open?: boolean;
	onChange?: DatePickerProps["onChange"];
	anchorEl: HTMLElement | null | undefined;
	onClose?: DatePickerProps["onClose"];
	format?: DatePickerProps["format"];
	stopPropagationForKeyDown?: boolean;
	disablePortal?: boolean;
	shouldDisableDate?: DatePickerProps["shouldDisableDate"];
	hideTextField?: boolean;
	hasHoverBackground?: boolean;
	/**
	 * Override for the date picker toolbar title text
	 */
	datePickerToolbarTitle?: string;
};

/**
 * SystemDatePickerPopup is a component that renders a date picker popup without a text field.
 *
 * NOTE: this popup does not support accessibility features like keyboard navigation - because it does not render a text field.
 * You can however provide accessibility features by wrapping the anchor element with something like this(in cases where the anchor element is not accessible by default):
 * <div tabIndex={0}
 * 		onKeyDown={(e) =>
 * 			if (event.key === "Enter" || event.key === " ") {
 * 				// toggle popup open state
 * 			}
 *     	}>
 *     	ref={anchorElRef}
 *     	/>
 * @param overridePickerProps
 * @param defaultValue
 * @param value
 * @param open
 * @param anchorEl
 * @param onChange
 * @param onClose
 * @param format - Format of the date when rendered in the input(s). Defaults to localized format based on the used views.
 * @param stopPropagationForKeyDown
 * @param disablePortal
 * @param shouldDisableDate
 * @param hasHoverBackground
 * @constructor
 */
export const SystemDatePickerPopup: FC<Props> = ({
	overridePickerProps,
	defaultValue,
	value,
	open,
	anchorEl,
	onChange,
	onClose,
	format,
	stopPropagationForKeyDown = true,
	disablePortal,
	shouldDisableDate,
	hideTextField = true,
	hasHoverBackground = false,
	datePickerToolbarTitle,
}) => {
	const { t } = useTranslation();
	const CustomDay = hasHoverBackground
		? (props: PickerDayProps) => {
				const { disabled, today, selected, ...other } = props;
				const useTodayStyle = today || (selected && disabled);
				return (
					<PickerDay
						{...other}
						disabled={disabled}
						selected={selected && !useTodayStyle}
						sx={(theme) => ({
							...(useTodayStyle
								? {
										border: `1px solid ${theme.palette.primary.main}`,
										backgroundColor: "transparent",
									}
								: disabled
									? {}
									: {
											backgroundColor: theme.palette.primary.light,
											"&:hover": {
												backgroundColor: theme.palette.primary.main,
												color: INK.ON_DARK,
											},
										}),
						})}
					/>
				);
			}
		: undefined;

	const slots: DatePickerProps["slots"] = {
		...(hasHoverBackground && CustomDay ? { day: CustomDay } : {}),
		...overridePickerProps?.slots,
	};

	return (
		<LocalizationProvider
			dateAdapter={AdapterLuxon}
			localeText={
				datePickerToolbarTitle
					? {
							datePickerToolbarTitle: datePickerToolbarTitle,
						}
					: undefined
			}
		>
			<DatePicker
				shouldDisableDate={shouldDisableDate}
				format={format}
				defaultValue={defaultValue}
				value={value}
				open={open}
				slots={slots}
				onClose={onClose}
				onChange={onChange}
				slotProps={{
					...(hideTextField ? { textField: { sx: { display: "none" } } } : {}),
					popper: {
						...portalOverlayProps("date-picker-popper"),
						...(anchorEl ? { anchorEl } : {}),
						onKeyDown: stopPropagationForKeyDown
							? (e: React.KeyboardEvent) => {
									e.stopPropagation();
								}
							: undefined,
						disablePortal: disablePortal,
						...(disablePortal
							? {
									onClick: (e: React.MouseEvent) => {
										e.stopPropagation();
									},
								}
							: {}),
					},
					toolbar: {
						toolbarPlaceholder: t("date.not.selected"),
					},

					...overridePickerProps?.slotProps,
				}}
				{...overridePickerProps}
			/>
		</LocalizationProvider>
	);
};
